<template>
	<div :class="classes" ref="dropdown" :style="styles">
		<div :class="selectorClasses"
			ref="selector"
			@click="onSelectorClicked"
		>
			<div class="teaming-drop-down__selector-slot left"
				:title="selectedOption.text"
			>
				<slot
					name="selected"
					:selectedOption="selectedOption"
					:onSelectorClicked="onSelectorClicked"
				>
					<div class="teaming-drop-down__default-text">
						{{ selectedOption.text }}
					</div>
				</slot>
			</div>
			<div class="teaming-drop-down__selector-slot right">
				<slot
					name="selector-icon"
					:selectedOption="selectedOption"
					:onSelectorClicked="onSelectorClicked"
				></slot>
				<FontAwesomeIcon
					:icon="['fas', 'sort-down']"
					class="teaming-drop-down__icon"
				/>
			</div>
		</div>
		<ul class="teaming-drop-down__options" ref="options">
			<li v-for="option in selectableOptions"
				:key="option[ altValueField ? altValueField : valueField ]"
				class="teaming-drop-down__option"
			>
				<slot name="options"
					:option="option"
					:selectedOption="selectedOption"
					:onOptionClicked="onOptionClicked"
				>
					<DropDownRow
						:option="option"
						:is-selected="option.value === selectedOption.value"
						@click="onOptionClicked( option, $event )"
					/>
				</slot>
			</li>
		</ul>
	</div>
</template>

<script>
import DropDownRow from "./DropDownRow.vue";

export const makeOption = ( {
	text = "",
	value = "",
	isPlaceholder = false,
	mark = "",
	count = "",
} = {} ) => ( {
	text,
	value,
	isPlaceholder,
	mark,
	count,
} );

export default {
	name: "TeamingDropDown",
	emits: [ "change", "opening", "opened", "closing", "closed", ],
	components: {
		DropDownRow,
	},
	props: {
		className: {
			type: String,
			default: "",
		},
		/** see: makeOption */
		options: {
			type: Array,
			required: true,
		},
		value: {
			type: String,
			default: "",
		},
		altValueField: {
			type: String,
			default: "",
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		width: {
			type: String,
			default: "",
		},
		isValid: {
			type: Boolean,
			default: true,
		},
	},
	data() {
		return {
			isOpen: false,
			documentClickHandler: null,
		};
	},
	computed: {
		classes() {
			return {
				"teaming-drop-down": true,
				"text-default": true,
				[ this.className ]: !!this.className,
			};
		},
		selectorClasses() {
			return {
				"teaming-drop-down__selector": true,
				"teaming-drop-down__selector--disabled": this.disabled,
				"teaming-drop-down__selector--focus": this.isOpen,
				"teaming-drop-down__selector--invalid": !this.isValid,
				"teaming-drop-down__selector--focused--invalid": this.isOpen && !this.isValid,
			};
		},
		styles() {
			const style = {};
			if ( this.width ) {
				style.width = this.width;
			}
			return style;
		},
		valueField() {
			return this.altValueField || "value";
		},
		selectableOptions() {
			return this.options.filter( option => !option.isPlaceholder );
		},
		selectedOption() {
			const { selectableOptions, value, valueField, } = this;

			return selectableOptions.find( option => option[ valueField ] === value ) ||
				selectableOptions[ 0 ] ||
				{
					text: "",
					[ valueField ]: "",
				};
		},
	},
	methods: {
		resize() {
			const { selector, options, } = this.$refs;
			if ( this.width ) {
				options.style.width = this.width;
				return;
			}
			const rect = selector.getBoundingClientRect();
			const { left, right, } = rect;
			options.style.width = `${ right - left }px`;
		},
		open() {
			this.$emit( "opening" );
			this.resize();
			this.isOpen = true;
			const { options, } = this.$refs;
			options.classList.add( "teaming-drop-down__options--visible" );
			this.$emit( "opened" );
		},
		close() {
			this.$emit( "closing" );
			this.resize();
			this.isOpen = false;
			const { options, } = this.$refs;
			// By default, the options are not scrollable, but if an extending component
			// changes that behavior, we want to ensure that they are scrolled back to
			// the top before the component is closed. Note that this needs to happen
			// before the visibility has been removed.
			options.scrollTo( { top: 0, left: 0, } );
			options.classList.remove( "teaming-drop-down__options--visible" );
			this.$emit( "closed" );
		},
		onSelectorClicked() {
			if ( this.disabled ) {
				return;
			}

			if ( this.isOpen ) {
				this.close();
			} else {
				this.open();
			}
		},
		onOptionClicked( option, e ) {
			e.stopPropagation();

			this.close();
			this.$emit( "change", { ...option, } );
		},
		createDocumentClickHandler() {
			if ( this.documentClickHandler ) {
				this.clearDocumentClickHandler();
			}
			// @deprecated: prefer foundation.global.onClickOutside() instead
			this.documentClickHandler = e => {
				const dropdownElement = this.$refs.dropdown;
				const path = e.path || ( e.composedPath && e.composedPath() );
				if ( !path.includes( dropdownElement ) ) {
					this.close();
				}
			};
			document.addEventListener( "click", this.documentClickHandler );
		},
		clearDocumentClickHandler() {
			if ( this.documentClickHandler ) {
				document.removeEventListener( "click", this.documentClickHandler );
				this.documentClickHandler = null;
			}
		},
	},
	mounted() {
		this.createDocumentClickHandler();
	},
	beforeUnmount() {
		this.clearDocumentClickHandler();
	},
	updated() {
		if ( this.disabled ) {
			this.$nextTick( () => this.close() );
		}
	},
};
</script>

<style lang="scss" scoped>
.teaming-drop-down {
	display: inline-block;
	position: relative;
}

.teaming-drop-down__default-text {
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;
	text-align: left;
}

.teaming-drop-down__selector {
	display: flex;
	justify-content: space-between;

	padding: 0.625rem 0.75rem; // 10px 12px
	background-color: $dropdown-background-color;
	border: 0.0625rem solid $dropdown-border-color; // 1px
	border-radius: 0.5rem; // 8px
	color: $dropdown-font-color;
	cursor: pointer;

	&:first-child {
		flex: 1;
	}

	&:hover {
		border: 0.0625rem solid $dropdown-border-color--hover;
	}
}

.teaming-drop-down__selector-slot {
	-webkit-user-select: none; /* Safari */
	-moz-user-select: none; /* Firefox */
	-ms-user-select: none; /* IE10+/Edge */
	user-select: none; /* Standard */
	overflow: hidden;
}

.teaming-drop-down__selector-slot.right {
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	align-items: center;
	height: 100%;
}

.teaming-drop-down__selector-slot.left {
	flex: 1;
}

.teaming-drop-down__selector--invalid {
	border-color: $dropdown-border-color--invalid;
	background-color: $dropdown-background-color--invalid;

	&:hover {
		border: 0.0625rem solid $dropdown-border-color--invalid--hover;
		border-radius: 0.5rem;
	}
}

.teaming-drop-down__selector--focus {
	border: 0.0625rem solid $dropdown-border-color--hover;
	box-shadow: 0rem 0rem 0rem .1875rem $dropdown-box-shadow-color--focus,
		0rem .1875rem .4375rem rgba(42, 47, 69, 0.1),
		0rem .3125rem .5rem -.0625rem rgba(42, 47, 69, 0.05);
}

.teaming-drop-down__selector--focused--invalid {
	border: 0.0625rem solid $dropdown-border-color--invalid--hover;
	border-radius: 0.5rem;
	box-shadow: 0 0 0 .1875rem $dropdown-box-shadow--invalid;
}

.teaming-drop-down__selector--disabled {
	background-color: $dropdown-background-color--disabled;
	border: 0.0625rem solid $dropdown-border-color--disabled;
	color: $dropdown-font-color--disabled;
	cursor: not-allowed;

	&:hover {
		border: 0.0625rem solid $dropdown-border-color--disabled;
	}
}

.teaming-drop-down__options {
	z-index: 10;
	list-style: none;
	margin: 0;
	padding: 0;
	background-color: $dropdown-background-color;
	border: 0.0625rem solid $dropdown-border-color; // 1px
	border-radius: 0.5rem; // 8px
	display: none;
	text-align: left;
	box-shadow: 0 0.1875rem 0.375rem 0.0625rem rgba(33, 45, 99, 0.1), 0 1.313rem 1.875rem -1.25rem rgba(33, 45, 99, 0.2);
	color: $dropdown-font-color;
	cursor: pointer;
}

// NOTE: padding/margins have to be applied at the option level to
// ensure that hover highlighting looks correct
.teaming-drop-down__option {
	padding: 0.375rem 1rem; // 6px 16px

	&:first-of-type {
		margin-top: 0.375rem; // 6px
	}

	&:last-of-type {
		margin-bottom: 0.375rem; // 6px
	}

	&:hover {
		background-color: $dropdown-background-color--hover;
	}
}

.teaming-drop-down__options--visible {
	display: block;
	position: absolute;
	width: 100%;
}

.teaming-drop-down__icon {
	margin-left: 1rem; // 16px
}
</style>
